import React from 'react';
import 'swiper/swiper.min.css';
import { useHistory, useParams } from 'react-router';
import { useGoBack } from '../../hooks/go-back';
import ZenLoading from '../../components/loading/Loading';
import { useSnackbar } from 'notistack';
import {
    SiteTheme,
    SiteThemeDataFragment,
    useGetSiteDesignQuery,
    useGetThemesQuery,
    useSiteAddDesignMutation,
} from '../../generated/types';
import SelectThemeBox from './SelectThemeBox';
import { ArrowBack } from '@menuzen/icon/build/outline';

const SelectThemePage: React.FC = () => {
    const history = useHistory();
    const { accountId } = useParams<{ accountId: string }>();
    const { loading: isLoadingDesign, data: design } = useGetSiteDesignQuery({
        fetchPolicy: 'no-cache',
    });
    const { enqueueSnackbar } = useSnackbar();

    const goBack = useGoBack();

    const [addDesign, { loading: isCreatingDesign }] = useSiteAddDesignMutation();
    const { data: themeData, loading: isLoadingThemeList } = useGetThemesQuery();

    const handleCreateMenu = async (theme: SiteThemeDataFragment) => {
        await addDesign({
            variables: {
                input: {
                    themeId: theme.id,
                },
            },
            onError: () => {
                enqueueSnackbar('Cannot create your website.', { variant: 'error' });
            },
            onCompleted: () => {
                history.push(`/account/${accountId}/site-design`);
            },
        });
    };

    const themeList = themeData?.siteThemes || [];

    if (isLoadingThemeList || isLoadingDesign)
        return (
            <div className="flex items-center justify-center flex-auto">
                <ZenLoading />
            </div>
        );

    if (design?.site) {
        history.push(`/account/${accountId}/home`);
        return <></>;
    }

    return (
        <div className="p-32 sm:p-60">
            <div className={`mb-24`}>
                <div
                    className="flex items-center text-primary cursor-pointer"
                    onClick={() => goBack(`/account/${accountId}/home`)}
                >
                    <span className="text-30 leading-none">
                        <ArrowBack />
                    </span>
                    <div className="ml-4">Dashboard</div>
                </div>
            </div>
            <h1 className="mb-10 text-25 leading-28 font-bold text-primary">Choose a template</h1>
            <p className="mb-24 text-14 leading-16 text-primary-80 -tracking-2">
                You can easily swap templates later if you change your mind!
            </p>
            {isLoadingThemeList ? (
                <div className="flex items-center justify-center absolute top-0 bottom-0 left-0 right-0">
                    <ZenLoading />
                </div>
            ) : (
                themeList.length > 0 && (
                    <>
                        <div className="flex flex-col overflow-y-auto">
                            <SelectThemeBox
                                handleCreateMenu={handleCreateMenu}
                                isAddingMenu={isCreatingDesign}
                                themes={themeList as SiteTheme[]}
                            />
                        </div>
                        <div className="fixed bottom-0 left-0 w-full h-60 bg-gradient-white-to-bottom"></div>
                    </>
                )
            )}
        </div>
    );
};

export default SelectThemePage;
